// RustDesk connection state machine, ported from the official web client
// (connection.ts) and adapted to our modules + baked-in config.
//
//   rendezvous(wss :base+2) --punch_hole_request--> relay_response
//     -> relay(wss :base+3) --request_relay--> secure handshake
//     -> hash -> login -> peer_info -> video/input loop

import { hbb } from "./proto/messages.js";
import Websock from "./websock";
import * as sha256 from "fast-sha256";
import {
  RS_PK,
  rendezvousUri,
  relayUri,
  relayUriFromAdvertised,
  type RustDeskConfig,
} from "./config";
import {
  verify,
  genBoxKeyPair,
  genSecretKey,
  seal,
} from "./crypto";
import { VideoRenderer, pickCodec } from "./video";
import { AudioPlayer } from "./audio";
import { decompress } from "./common";

export interface CursorImage {
  hotx: number;
  hoty: number;
  width: number;
  height: number;
  rgba: Uint8Array; // width*height*4
}

export interface RemoteEntry {
  name: string;
  isDir: boolean;
  size: number;
  modified: number;
}

export type Status =
  | "idle"
  | "connecting"
  | "rendezvous"
  | "relay"
  | "handshake"
  | "password"
  | "logging-in"
  | "connected"
  | "closed"
  | "error";

export interface ConnectionCallbacks {
  onStatus?: (s: Status, detail?: string) => void;
  onError?: (title: string, text: string) => void;
  onSecure?: (secure: boolean) => void;
  onPeerInfo?: (pi: hbb.IPeerInfo) => void;
  onPasswordRequired?: (wrong: boolean) => void;
  onFirstFrame?: () => void;
  onCursor?: (c: CursorImage | null) => void; // null => fall back to default arrow
  onCursorPosition?: (x: number, y: number) => void; // host-reported cursor position
  onClipboard?: (text: string) => void; // remote clipboard text -> write locally
  onDisplaySwitched?: (display: number) => void; // host confirmed active display
  onFileDir?: (path: string, entries: RemoteEntry[]) => void;
  onDownload?: (name: string, blob: Blob) => void;
  onFileError?: (msg: string) => void;
  onFileNote?: (msg: string) => void; // informational (e.g. upload complete)
}

function hash(parts: (string | Uint8Array)[]): Uint8Array {
  const h = new sha256.Hash();
  for (const p of parts) h.update(typeof p === "string" ? new TextEncoder().encode(p) : p);
  return h.digest();
}

export default class Connection {
  private cfg: RustDeskConfig;
  private cb: ConnectionCallbacks;
  private ws: Websock | undefined;
  private id = "";
  private hashMsg: hbb.IHash | undefined;
  private password: Uint8Array | undefined; // salted (sha256(pw+salt))
  private peerInfo: hbb.IPeerInfo | undefined;
  private firstFrame = false;
  private video: VideoRenderer;
  private audio = new AudioPlayer();
  private closed = false;
  private videoWatchdog: any;
  private nudged = false;
  private cursors = new Map<number, CursorImage>();
  private fileJobs = new Map<number, { name: string; parts: Uint8Array[] }>();
  private nextFileId = 1;
  private fileMode: boolean;

  constructor(
    canvas: HTMLCanvasElement,
    cb: ConnectionCallbacks,
    cfg: RustDeskConfig,
    opts: { fileMode?: boolean } = {}
  ) {
    this.cfg = cfg;
    this.cb = cb;
    this.fileMode = opts.fileMode ?? false;
    this.video = new VideoRenderer(canvas, (e) => console.error("[video]", e));
  }

  private status(s: Status, detail?: string) {
    this.cb.onStatus?.(s, detail);
  }

  async start(id: string) {
    this.id = id;
    this.closed = false;
    try {
      await this.rendezvous(id);
    } catch (e: any) {
      if (this.closed) return;
      this.status("error", String(e?.message ?? e));
      this.cb.onError?.("Connection Error", String(e?.message ?? e));
    }
  }

  private async rendezvous(id: string) {
    const uri = rendezvousUri(this.cfg);
    this.status("rendezvous", uri);
    const ws = new Websock(uri, true);
    await ws.open();

    const punch_hole_request = hbb.PunchHoleRequest.create({
      id,
      licence_key: this.cfg.key || undefined,
      conn_type: this.fileMode ? hbb.ConnType.FILE_TRANSFER : hbb.ConnType.DEFAULT_CONN,
      nat_type: hbb.NatType.SYMMETRIC,
    });
    ws.sendRendezvous({ punch_hole_request });

    const msg = (await ws.next()) as hbb.RendezvousMessage;
    ws.close();

    const phr = msg.punch_hole_response;
    const rr = msg.relay_response;
    if (phr) {
      this.handlePunchHoleFailure(phr);
      return;
    }
    if (rr) {
      if (!rr.version) {
        this.cb.onError?.("Error", "Remote version is too low / not web-capable");
        return;
      }
      await this.connectRelay(rr);
    } else {
      this.cb.onError?.("Error", "Unexpected rendezvous response");
    }
  }

  private handlePunchHoleFailure(phr: hbb.IPunchHoleResponse) {
    if (phr.other_failure) return this.cb.onError?.("Error", phr.other_failure);
    switch (phr.failure) {
      case hbb.PunchHoleResponse.Failure.ID_NOT_EXIST:
        return this.cb.onError?.("Error", "ID does not exist");
      case hbb.PunchHoleResponse.Failure.OFFLINE:
        return this.cb.onError?.("Error", "Remote desktop is offline");
      case hbb.PunchHoleResponse.Failure.LICENSE_MISMATCH:
        return this.cb.onError?.("Error", "Key mismatch");
      case hbb.PunchHoleResponse.Failure.LICENSE_OVERUSE:
        return this.cb.onError?.("Error", "Key overuse");
      default:
        return this.cb.onError?.("Error", "Punch hole failed");
    }
  }

  private async connectRelay(rr: hbb.IRelayResponse) {
    const uri = rr.relay_server
      ? relayUriFromAdvertised(rr.relay_server, this.cfg)
      : relayUri(this.cfg);
    this.status("relay", uri);
    const ws = new Websock(uri, false);
    await ws.open();
    this.ws = ws;

    ws.sendRendezvous({
      request_relay: hbb.RequestRelay.create({
        licence_key: this.cfg.key || undefined,
        uuid: rr.uuid,
      }),
    });

    const secure = await this.secure(rr.pk ?? undefined);
    this.cb.onSecure?.(secure);
    await this.msgLoop();
  }

  /** RustDesk trust chain: server signs peer's sign-key; peer signs its box-key. */
  private async secure(pk: Uint8Array | undefined): Promise<boolean> {
    this.status("handshake");
    const sendEmpty = () => this.ws?.sendMessage({ public_key: hbb.PublicKey.create({}) });

    // Step 1: verify the rendezvous-provided pk with the server key -> peer sign pk.
    let signPk: Uint8Array | undefined;
    if (pk && pk.length) {
      try {
        const opened = verify(pk, this.cfg.key || RS_PK);
        const idpk = hbb.IdPk.decode(opened);
        if (idpk.id === this.id && idpk.pk && idpk.pk.length === 32) {
          signPk = idpk.pk instanceof Uint8Array ? idpk.pk : new Uint8Array(idpk.pk);
        }
      } catch (e) {
        console.error("rendezvous pk verify failed", e);
      }
    }
    if (!signPk) {
      // No/invalid key => fall back to an unencrypted session.
      sendEmpty();
      return false;
    }

    // Step 2: read peer's SignedId, verify with peer sign pk -> IdPk{id, boxPk}.
    const msg = (await this.ws!.next()) as hbb.Message;
    const signedId = msg.signed_id;
    if (!signedId?.id) {
      console.error("handshake: expected signed_id");
      sendEmpty();
      return false;
    }
    let theirBoxPk: Uint8Array;
    try {
      const opened = verify(
        signedId.id instanceof Uint8Array ? signedId.id : new Uint8Array(signedId.id),
        signPk
      );
      const idpk = hbb.IdPk.decode(opened);
      if (idpk.id !== this.id) {
        console.error("handshake: id mismatch");
        sendEmpty();
        return false;
      }
      theirBoxPk = idpk.pk instanceof Uint8Array ? idpk.pk : new Uint8Array(idpk.pk);
      if (theirBoxPk.length !== 32) {
        console.error("handshake: bad box pk length");
        sendEmpty();
        return false;
      }
    } catch (e) {
      console.error("handshake: signed_id verify failed", e);
      sendEmpty();
      return false;
    }

    // Step 3: seal a fresh session key to the peer, switch to encrypted transport.
    const [mySk, myPk] = genBoxKeyPair();
    const secretKey = genSecretKey();
    const symmetric_value = seal(secretKey, theirBoxPk, mySk);
    this.ws!.sendMessage({
      public_key: hbb.PublicKey.create({ asymmetric_value: myPk, symmetric_value }),
    });
    this.ws!.setSecretKey(secretKey);
    return true;
  }

  private async msgLoop() {
    while (!this.closed) {
      const msg = (await this.ws!.next()) as hbb.Message;
      if (msg.hash) {
        this.hashMsg = msg.hash;
        if (!this.password) {
          this.status("password");
          this.cb.onPasswordRequired?.(false);
        } else {
          this.login();
        }
      } else if (msg.test_delay) {
        if (!msg.test_delay.from_client) this.ws!.sendMessage({ test_delay: msg.test_delay });
      } else if (msg.login_response) {
        const r = msg.login_response;
        if (r.error) {
          if (r.error === "Wrong Password") {
            this.password = undefined;
            this.status("password");
            this.cb.onPasswordRequired?.(true);
          } else {
            this.status("error", r.error);
            this.cb.onError?.("Login Error", r.error);
          }
        } else if (r.peer_info) {
          this.handlePeerInfo(r.peer_info);
        }
      } else if (msg.peer_info) {
        this.handlePeerInfo(msg.peer_info);
      } else if (msg.video_frame) {
        this.handleVideoFrame(msg.video_frame);
      } else if (msg.cursor_data) {
        this.handleCursorData(msg.cursor_data);
      } else if (msg.cursor_id != null) {
        console.log("[cursor] id", Number(msg.cursor_id), this.cursors.has(Number(msg.cursor_id)) ? "(cached)" : "(MISS)");
        this.applyCursor(Number(msg.cursor_id));
      } else if (msg.cursor_position) {
        this.cb.onCursorPosition?.(Number(msg.cursor_position.x ?? 0), Number(msg.cursor_position.y ?? 0));
      } else if (msg.clipboard) {
        this.handleClipboard(msg.clipboard);
      } else if (msg.multi_clipboards) {
        for (const cb of msg.multi_clipboards.clipboards ?? []) this.handleClipboard(cb);
      } else if (msg.audio_frame) {
        const d = msg.audio_frame.data;
        if (d) this.audio.feed(d instanceof Uint8Array ? d : new Uint8Array(d));
      } else if (msg.file_response) {
        this.handleFileResponse(msg.file_response);
      } else if (msg.misc) {
        if (!this.handleMisc(msg.misc)) break;
      }
    }
  }

  private handleClipboard(cb: hbb.IClipboard) {
    // Text only for now (format 0 = Text). Skip images/rtf/html.
    if (cb.format != null && cb.format !== hbb.ClipboardFormat.Text) return;
    let content = cb.content instanceof Uint8Array ? cb.content : new Uint8Array(cb.content ?? []);
    if (cb.compress) content = decompress(content);
    if (!content.length) return;
    try {
      const text = new TextDecoder().decode(content);
      if (text) this.cb.onClipboard?.(text);
    } catch (e) {
      console.error("[clipboard] decode failed", e);
    }
  }

  /** Send local clipboard text to the host (so a remote paste uses it). */
  sendClipboardText(text: string) {
    if (!text) return;
    const content = new TextEncoder().encode(text);
    this.ws?.sendMessage({
      clipboard: hbb.Clipboard.create({ content, format: hbb.ClipboardFormat.Text, compress: false }),
    });
  }

  private handleCursorData(cd: hbb.ICursorData) {
    const compressed = cd.colors instanceof Uint8Array ? cd.colors : new Uint8Array(cd.colors ?? []);
    const rgba = decompress(compressed);
    const img: CursorImage = {
      hotx: cd.hotx ?? 0,
      hoty: cd.hoty ?? 0,
      width: cd.width ?? 0,
      height: cd.height ?? 0,
      rgba,
    };
    console.log(`[cursor] data id=${Number(cd.id ?? 0)} ${img.width}x${img.height} hot=${img.hotx},${img.hoty}`);
    this.cursors.set(Number(cd.id ?? 0), img);
    this.cb.onCursor?.(img);
  }

  private applyCursor(id: number) {
    const img = this.cursors.get(id);
    // Cache miss (host referenced a cursor whose image we never received): show
    // a normal arrow rather than a stale/blank cursor.
    this.cb.onCursor?.(img ?? null);
  }

  private handleVideoFrame(vf: hbb.IVideoFrame) {
    const picked = pickCodec(vf);
    if (!this.firstFrame) {
      console.log("[video] first frame:", picked ? picked.kind : "UNKNOWN codec", "display", vf.display);
      this.firstFrame = true;
      clearTimeout(this.videoWatchdog);
      this.status("connected");
      this.cb.onFirstFrame?.();
    }
    if (!picked) {
      console.warn("[video] frame with no codec we handle:", Object.keys(vf));
      return;
    }
    this.video.decode(picked.kind, picked.frames);
    // Ack so the controlled side keeps streaming (video_ack_required=true).
    this.ws!.sendMessage({ misc: hbb.Misc.create({ video_received: true }) });
  }

  private handlePeerInfo(pi: hbb.IPeerInfo) {
    this.peerInfo = pi;
    if (this.fileMode) {
      // File-transfer session: no displays/video, just signal ready.
      this.status("connected", "file session ready");
      this.cb.onPeerInfo?.(pi);
      return;
    }
    if (!pi.displays || pi.displays.length === 0) {
      this.cb.onError?.("Remote Error", "No display");
      return;
    }
    this.status("logging-in", "waiting for image");
    this.cb.onPeerInfo?.(pi);
    // Watchdog: if no frame arrives shortly, nudge the host with refresh_video
    // (kicks a fresh keyframe), then flag it so the UI/console shows a hint.
    clearTimeout(this.videoWatchdog);
    this.videoWatchdog = setTimeout(() => {
      if (this.firstFrame || this.closed) return;
      if (!this.nudged) {
        this.nudged = true;
        console.warn("[video] no frame 3s after peer_info — sending refresh_video");
        this.refresh();
        this.videoWatchdog = setTimeout(() => {
          if (this.firstFrame || this.closed) return;
          console.warn("[video] still no frame — host may not be encoding a codec we requested");
          this.status("logging-in", "connected, but no video from host");
        }, 4000);
      }
    }, 3000);
  }

  private handleMisc(misc: hbb.IMisc): boolean {
    if (misc.close_reason) {
      this.cb.onError?.("Connection Error", misc.close_reason);
      this.close();
      return false;
    }
    if (misc.switch_display) {
      // New display => new resolution/codec context; wait for a fresh keyframe.
      this.video.reset();
      this.cb.onDisplaySwitched?.(misc.switch_display.display ?? 0);
    }
    if (misc.audio_format) {
      this.audio.init(Number(misc.audio_format.sample_rate ?? 0), Number(misc.audio_format.channels ?? 0));
    }
    return true;
  }

  /** Resume the audio context (call from a user gesture if it got suspended). */
  resumeAudio() {
    this.audio.resume();
  }

  setAudioMuted(m: boolean) {
    this.audio.setMuted(m);
  }

  // ---- login ----------------------------------------------------------------

  login(clearPassword?: string) {
    let p: Uint8Array | undefined;
    if (clearPassword) {
      const salt = this.hashMsg?.salt ?? "";
      this.password = hash([clearPassword, salt]);
      const challenge = this.hashMsg?.challenge ?? "";
      p = hash([this.password, challenge]);
    } else if (this.password) {
      const challenge = this.hashMsg?.challenge ?? "";
      p = hash([this.password, challenge]);
    }
    this.status("logging-in");
    let login_request: hbb.ILoginRequest;
    if (this.fileMode) {
      // Dedicated file-transfer session (host gates FileAction on this).
      login_request = hbb.LoginRequest.create({
        username: this.id,
        my_id: "web",
        my_name: "web",
        password: p,
        file_transfer: hbb.FileTransfer.create({ dir: "", show_hidden: false }),
      });
    } else {
      // Advertise what we can decode so the host encodes a codec we support.
      const supported_decoding = hbb.SupportedDecoding.create({
        ability_vp9: 1,
        ability_vp8: 1,
        ability_av1: 1,
        prefer: hbb.SupportedDecoding.PreferCodec.VP9,
      });
      const option = hbb.OptionMessage.create({ supported_decoding });
      login_request = hbb.LoginRequest.create({
        username: this.id,
        my_id: "web",
        my_name: "web",
        password: p,
        option,
        video_ack_required: true,
      });
    }
    this.ws!.sendMessage({ login_request });
  }

  // ---- input ------------------------------------------------------------------

  private mod(alt: boolean, ctrl: boolean, shift: boolean, meta: boolean): hbb.ControlKey[] {
    const m: hbb.ControlKey[] = [];
    if (alt) m.push(hbb.ControlKey.Alt);
    if (ctrl) m.push(hbb.ControlKey.Control);
    if (shift) m.push(hbb.ControlKey.Shift);
    if (meta) m.push(hbb.ControlKey.Meta);
    return m;
  }

  inputMouse(
    mask = 0,
    x = 0,
    y = 0,
    alt = false,
    ctrl = false,
    shift = false,
    meta = false
  ) {
    const mouse_event = hbb.MouseEvent.create({ mask, x, y, modifiers: this.mod(alt, ctrl, shift, meta) });
    this.ws?.sendMessage({ mouse_event });
  }

  /** Relative mouse move (MOUSE_TYPE_MOVE_RELATIVE=5). Used to force Windows to
   *  redraw the host cursor, which absolute moves don't do over the bare desktop. */
  inputMouseRelative(dx = 0, dy = 0) {
    this.ws?.sendMessage({ mouse_event: hbb.MouseEvent.create({ mask: 5, x: dx, y: dy }) });
  }

  /** Type a literal string on the remote (simplest reliable keyboard path). */
  inputString(seq: string) {
    this.ws?.sendMessage({ key_event: hbb.KeyEvent.create({ seq, press: true }) });
  }

  /** Send a character key by code with modifiers (for shortcuts: Ctrl+C/V/A…).
   *  Matches RustDesk web: `chr` is the (lowercase) character's ASCII code. */
  inputKeyChr(
    chr: number,
    down = true,
    press = true,
    alt = false,
    ctrl = false,
    shift = false,
    meta = false
  ) {
    this.ws?.sendMessage({
      key_event: hbb.KeyEvent.create({ chr, down, press, modifiers: this.mod(alt, ctrl, shift, meta) }),
    });
  }

  /** Press/release a named control key (Enter, Backspace, arrows, …). */
  inputControlKey(
    ck: hbb.ControlKey,
    down = true,
    press = false,
    alt = false,
    ctrl = false,
    shift = false,
    meta = false
  ) {
    this.ws?.sendMessage({
      key_event: hbb.KeyEvent.create({
        control_key: ck,
        down,
        press,
        modifiers: this.mod(alt, ctrl, shift, meta),
      }),
    });
  }

  get displaySize(): { width: number; height: number } | undefined {
    const d = this.peerInfo?.displays?.[0];
    return d ? { width: d.width ?? 0, height: d.height ?? 0 } : undefined;
  }

  // ---- file transfer (over the existing session) ----------------------------

  private handleFileResponse(fr: hbb.IFileResponse) {
    if (fr.dir) {
      // A download sends a metadata `dir` (id = the job id) right before its
      // blocks. That is NOT a directory-listing navigation — ignore it, or we'd
      // set the browser path to the downloaded file's path.
      if (this.fileJobs.has(Number(fr.dir.id ?? 0))) return;
      const entries: RemoteEntry[] = (fr.dir.entries ?? []).map((e) => ({
        name: e.name ?? "",
        isDir:
          e.entry_type === hbb.FileType.Dir ||
          e.entry_type === hbb.FileType.DirLink ||
          e.entry_type === hbb.FileType.DirDrive,
        size: Number(e.size ?? 0),
        modified: Number(e.modified_time ?? 0),
      }));
      this.cb.onFileDir?.(fr.dir.path ?? "", entries);
    } else if (fr.block) {
      const job = this.fileJobs.get(Number(fr.block.id));
      if (job) {
        let data = fr.block.data instanceof Uint8Array ? fr.block.data : new Uint8Array(fr.block.data ?? []);
        if (fr.block.compressed) data = decompress(data);
        job.parts.push(data);
      }
    } else if (fr.done) {
      const id = Number(fr.done.id);
      const job = this.fileJobs.get(id);
      if (job) {
        this.fileJobs.delete(id);
        this.cb.onDownload?.(job.name, new Blob(job.parts as BlobPart[]));
      }
    } else if (fr.digest) {
      // Overwrite detection (rare given our version). For a download, proceed
      // from the start; we never already have the file.
      if (!fr.digest.is_upload) {
        this.ws?.sendMessage({
          file_action: hbb.FileAction.create({
            send_confirm: hbb.FileTransferSendConfirmRequest.create({
              id: fr.digest.id,
              file_num: fr.digest.file_num,
              offset_blk: 0,
            }),
          }),
        });
      }
    } else if (fr.error) {
      this.cb.onFileError?.(fr.error.error ?? "file error");
    }
  }

  /** List a remote directory ("" => drives on Windows / root on *nix). */
  listDir(path: string) {
    this.ws?.sendMessage({
      file_action: hbb.FileAction.create({ read_dir: hbb.ReadDir.create({ path, include_hidden: false }) }),
    });
  }

  /** Download a remote file → delivered to onDownload(name, Blob). */
  download(path: string) {
    const name = path.split(/[\\/]/).pop() || "download";
    const id = this.nextFileId++;
    this.fileJobs.set(id, { name, parts: [] });
    this.ws?.sendMessage({
      file_action: hbb.FileAction.create({
        send: hbb.FileTransferSendRequest.create({ id, path, file_num: 0, include_hidden: false }),
      }),
    });
  }

  /** Upload a local File into remoteDir on the host. */
  async upload(remoteDir: string, file: File) {
    const id = this.nextFileId++;
    const buf = new Uint8Array(await file.arrayBuffer());
    const modified = Math.floor((file.lastModified || Date.now()) / 1000);
    this.ws?.sendMessage({
      file_action: hbb.FileAction.create({
        receive: hbb.FileTransferReceiveRequest.create({
          id,
          path: remoteDir,
          file_num: 0,
          total_size: buf.length,
          files: [
            hbb.FileEntry.create({
              name: file.name,
              size: buf.length,
              modified_time: modified,
              entry_type: hbb.FileType.File,
            }),
          ],
        }),
      }),
    });
    const CHUNK = 64 * 1024;
    let blk = 0;
    for (let off = 0; off < buf.length; off += CHUNK) {
      const slice = buf.subarray(off, Math.min(off + CHUNK, buf.length));
      this.ws?.sendMessage({
        file_response: hbb.FileResponse.create({
          block: hbb.FileTransferBlock.create({ id, file_num: 0, data: slice, compressed: false, blk_id: blk++ }),
        }),
      });
    }
    this.ws?.sendMessage({
      file_response: hbb.FileResponse.create({ done: hbb.FileTransferDone.create({ id, file_num: 0 }) }),
    });
    this.cb.onFileNote?.(`Uploaded ${file.name}`);
  }

  /** Change image quality live (Low/Balanced/Best). */
  setImageQuality(q: "low" | "balanced" | "best") {
    const map = {
      low: hbb.ImageQuality.Low,
      balanced: hbb.ImageQuality.Balanced,
      best: hbb.ImageQuality.Best,
    } as const;
    const image_quality = map[q] ?? hbb.ImageQuality.Balanced;
    this.ws?.sendMessage({ misc: hbb.Misc.create({ option: hbb.OptionMessage.create({ image_quality }) }) });
  }

  /** Change the max capture FPS live. */
  setFps(fps: number) {
    this.ws?.sendMessage({ misc: hbb.Misc.create({ option: hbb.OptionMessage.create({ custom_fps: fps }) }) });
  }

  /** Ask the host to switch its captured monitor. */
  switchDisplay(index: number) {
    const switch_display = hbb.SwitchDisplay.create({ display: index });
    this.ws?.sendMessage({ misc: hbb.Misc.create({ switch_display }) });
  }

  refresh() {
    this.ws?.sendMessage({ misc: hbb.Misc.create({ refresh_video: true }) });
  }

  close() {
    this.closed = true;
    this.ws?.close();
    this.video.close();
    this.audio.close();
    this.status("closed");
  }
}
